import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { MessageCircle, Send, Clock, Check, Calendar } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

interface ClientCheckInsViewProps {
  clientId: string;
  clinicianId: string;
}

export function ClientCheckInsView({ clientId, clinicianId }: ClientCheckInsViewProps) {
  const queryClient = useQueryClient();
  const [respondingTo, setRespondingTo] = useState<string | null>(null);
  const [response, setResponse] = useState("");

  const { data: checkIns, isLoading } = useQuery({
    queryKey: ['client-check-ins-view', clientId, clinicianId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('clinician_check_in_prompts')
        .select('*')
        .eq('client_id', clientId)
        .eq('clinician_id', clinicianId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    }
  });

  const respondMutation = useMutation({
    mutationFn: async ({ id, text }: { id: string; text: string }) => {
      const { error } = await supabase
        .from('clinician_check_in_prompts')
        .update({ 
          client_response: text,
          responded_at: new Date().toISOString(),
          status: 'responded'
        })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-check-ins-view'] });
      setRespondingTo(null);
      setResponse("");
      toast.success('Response sent!');
    }
  });

  const pendingCheckIns = checkIns?.filter(c => c.status === 'pending' || c.status === 'sent') || [];
  const respondedCheckIns = checkIns?.filter(c => c.status === 'responded') || [];

  if (isLoading) {
    return (
      <Card className="bg-card/50 border-border/30">
        <CardContent className="py-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Pending Check-ins */}
      <Card className="bg-card/50 border-border/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5 text-primary" />
            Check-In Prompts
          </CardTitle>
        </CardHeader>
        <CardContent>
          {pendingCheckIns.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <MessageCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No pending check-ins</p>
            </div>
          ) : (
            <div className="space-y-4">
              {pendingCheckIns.map(checkIn => (
                <div key={checkIn.id} className="p-4 rounded-lg bg-primary/5 border border-primary/20">
                  <div className="flex items-start justify-between mb-2">
                    <Badge className="bg-primary/10 text-primary">
                      <Clock className="h-3 w-3 mr-1" />
                      Awaiting Response
                    </Badge>
                    {checkIn.scheduled_date && (
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {format(new Date(checkIn.scheduled_date), 'MMM d')}
                      </span>
                    )}
                  </div>
                  
                  <p className="text-foreground font-medium mt-3 mb-4">
                    {checkIn.prompt_text}
                  </p>

                  {respondingTo === checkIn.id ? (
                    <div className="space-y-2">
                      <Textarea
                        value={response}
                        onChange={(e) => setResponse(e.target.value)}
                        placeholder="Type your response..."
                        className="bg-background"
                        rows={4}
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => respondMutation.mutate({ id: checkIn.id, text: response })}
                          disabled={!response.trim() || respondMutation.isPending}
                        >
                          <Send className="h-4 w-4 mr-1" />
                          Send Response
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => { setRespondingTo(null); setResponse(""); }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => setRespondingTo(checkIn.id)}
                    >
                      Respond
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Previous Responses */}
      {respondedCheckIns.length > 0 && (
        <Card className="bg-card/50 border-border/30">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Check className="h-5 w-5 text-green-500" />
              Previous Responses ({respondedCheckIns.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {respondedCheckIns.slice(0, 5).map(checkIn => (
                <div key={checkIn.id} className="p-3 rounded-lg bg-muted/20 border border-border/20">
                  <p className="text-sm font-medium text-foreground mb-1">
                    {checkIn.prompt_text}
                  </p>
                  <p className="text-sm text-muted-foreground italic">
                    "{checkIn.client_response}"
                  </p>
                  <span className="text-xs text-muted-foreground mt-2 block">
                    {checkIn.responded_at && format(new Date(checkIn.responded_at), 'MMM d, yyyy')}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
